import React, { useCallback, useEffect, useState } from "react";
import { EventParamTypes, formInputInfo } from "../../types/types";
import { Loading } from "../../components/utils/Loading";
import { ErrorFetching } from "../../components/modals/ErrorFetching";
import { CreateRegistrationQuestions } from "./components/CreateRegistrationQuestions";
import { useDispatch, useSelector } from "react-redux";
import { Route, useLocation, useParams, useRouteMatch } from "react-router-dom";
import Tab from "../../components/tabs/Tab";
import { Tabs } from "../../components/tabs/Tabs";
import { EventInformationForm } from "./components/EventInformationForm";
import allActions from "../../actions/allActions";
import { PageStylingForm } from "./components/PageStylingForm";
import PreviewForm from "./components/PreviewForm";
import * as eState from "../eventReducer";
import * as fState from "../../reducers/formReducer";
import * as erState from "../../reducers/errorReducer";
import { useFormatMessage } from "../../utils/functions";
import { ValidationList } from "./components/ValidationList";
import { useNotify } from "../../hooks/useNotify";
import { BreadRoute } from "../../components/breadcrumbs/BreadRoute";
import { BreadCrumb } from "../../components/breadcrumbs/BreadCrumb";
import SaveQuestions from "./components/SaveQuestions";

export const CreateRegistrationContainer: React.FC = () => {
  const eventState = useSelector(eState.getEventState);
  const previewItems = useSelector(fState.getPreviewFormItems);
  const { initLoading } = useSelector(fState.getFormState);
  const { message } = useSelector(erState.getErrorMessage);
  const errorMsg = useNotify(message);
  const { id, subEventId } = useParams<EventParamTypes>();
  const dispatch = useDispatch();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(null);
  const { path, url } = useRouteMatch();
  const hasForm = useSelector(eState.selectHasSubEventForm);
  const previewFormName = "previewRegistrationForm";
  const basicInformationTab = useFormatMessage("Registration.Tabs.basic");
  const pageStylingTab = useFormatMessage("Registration.Tabs.styling");
  const registrationQuestionsTab = useFormatMessage(
    "Registration.Tabs.Questions"
  );
  const registrationValidationTab = useFormatMessage(
    "Registration.Tabs.Validation"
  );
  const previewTab = useFormatMessage("Registration.CertPage.Preview");
  const saveTab = useFormatMessage("Registration.CertPage.Save");

  const getActiveTab = useCallback(() => {
    const pathArray = location.pathname.split("/");
    return pathArray.length === 6
      ? setActiveTab(basicInformationTab)
      : setActiveTab(pathArray[pathArray.length - 1]);
  }, [location, basicInformationTab]);

  const confirmRegistrationQuestions = () => {
    dispatch(
      allActions.eventActions.startSaveRegistrationQuestions(previewItems)
    );
  };

  const getForm = useCallback(() => {
    if (!hasForm) {
      dispatch(allActions.eventActions.startDraggableFormFetch("122", "fi"));
    }
  }, [dispatch, hasForm]);

  useEffect(() => {
    getForm();
  }, [getForm]);

  useEffect(() => {
    getActiveTab();
  }, [getActiveTab]);

  return initLoading || eventState.initLoading ? (
    <Loading styles="items-center m-auto" />
  ) : (
    <div className="container w-screen h-screen">
      <BreadRoute>
        <BreadCrumb label={"menu"} link={"/eventmenu"} />
        <BreadCrumb label={"events"} link={"/events"} />
        <BreadCrumb label={`event ${id}`} link={`/events/${id}`} />
        <BreadCrumb label={`subevents`} link={`/events/${id}/subevents/`} />
        <BreadCrumb
          label={`subevent ${subEventId}`}
          link={`/events/${id}/subevents/${subEventId}`}
        />
        <BreadCrumb
          label={`registration`}
          link={`/events/${id}/subevents/${subEventId}/registration`}
          lastChild={true}
        />
      </BreadRoute>
      <Tabs>
        <Tab
          activeLabel={activeTab}
          label={basicInformationTab}
          link={`${url}`}
        />
        <Tab
          activeLabel={activeTab}
          label={pageStylingTab}
          link={`${url}/${pageStylingTab}`}
        />
        <Tab
          activeLabel={activeTab}
          label={registrationQuestionsTab}
          link={`${url}/${registrationQuestionsTab}`}
        />
        <Tab
          activeLabel={activeTab}
          label={registrationValidationTab}
          link={`${url}/${registrationValidationTab}`}
        />
        <Tab
          activeLabel={activeTab}
          label={previewTab}
          link={`${url}/${previewTab}`}
        />
        <Tab
          activeLabel={activeTab}
          label={saveTab}
          link={`${url}/${saveTab}`}
        />
      </Tabs>
      <div className="container h-full max-h-11/12 w-full flex justify-center border-r border-l border-b border-gray-400 overflow-auto scroller">
        <ErrorFetching message={errorMsg} />
        <Route exact path={`${path}`} render={() => <EventInformationForm />} />
        <Route
          path={`${path}/${registrationQuestionsTab}`}
          render={() => (
            <CreateRegistrationQuestions previewFormName={previewFormName} />
          )}
        />
        <Route
          path={`${path}/${pageStylingTab}`}
          render={() => <PageStylingForm />}
        />
        <Route
          path={`${path}/${registrationValidationTab}`}
          render={() => <ValidationList />}
        />
        <Route
          path={`${path}/${previewTab}`}
          render={() => <PreviewForm formName={previewFormName} />}
        />
        <Route
          path={`${path}/${saveTab}`}
          render={() => (
            <SaveQuestions confirm={confirmRegistrationQuestions} />
          )}
        />
      </div>
    </div>
  );
};

export default CreateRegistrationContainer;
